import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Stack,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  Chip,
  Card,
  CardMedia,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { searchAPI } from "../API/Seach";
import Loader from "../Components/Loader";
import {
  GuestDecorLeftColumn,
  GuestDecorRightColumn,
} from "../Components/GuestDetail/GuestDetailSideDecor";
import { tokens } from "../theme/tokens";

const themePrimary = tokens.brand;
const themeAccent = tokens.accent;

const NAME_PATTERN = /^[A-Za-z]+(?:\s+[A-Za-z]+)*$/;
const MOBILE_PATTERN = /^\d{10}$/;

const formatDateDDMMYYYY = (value) => {
  const v = String(value || "").trim();
  if (!v) return "—";
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const GuestDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    bookingDetails: initialBookingDetails,
    hotelInfo: passedHotelInfo,
    selectedRoomSummary,
    guestDetailsDraft,
  } = location.state || {};

  const [bookingDetails] = useState(initialBookingDetails);
  const [guestDetails, setGuestDetails] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const updateGuestField = useCallback(
    (roomIndex, guestNumber, guestType, field, value) => {
      setGuestDetails((prev) => {
        const next = [...prev];
        const idx = next.findIndex(
          (g) =>
            g.roomIndex === roomIndex &&
            g.guestNumber === guestNumber &&
            g.guestType === guestType
        );
        if (idx !== -1) {
          const nextValue =
            field === "panCardNumber" ? String(value).toUpperCase() : value;
          next[idx] = { ...next[idx], [field]: nextValue };
        }
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (Array.isArray(guestDetailsDraft) && guestDetailsDraft.length > 0) {
      setGuestDetails(guestDetailsDraft);
      return;
    }
    if (bookingDetails?.query?.occupancies?.length) {
      const initialGuestDetails = bookingDetails.query.occupancies.flatMap(
        (occ, roomIndex) => {
          const roomGuests = [];
          let guestCounter = 1;
          const adults = occ.numOfAdults || 0;
          const childAges = occ.childAges || [];
          for (let i = 0; i < adults; i++) {
            roomGuests.push({
              roomIndex,
              guestType: "adult",
              guestNumber: guestCounter++,
              title: "",
              firstName: "",
              lastName: "",
              mobileNumber: "",
              email: "",
              panCardNumber: "",
              passportNumber: "",
              passportExpiry: "",
              age: null,
            });
          }
          for (let i = 0; i < childAges.length; i++) {
            roomGuests.push({
              roomIndex,
              guestType: "child",
              guestNumber: guestCounter++,
              title: "",
              firstName: "",
              lastName: "",
              mobileNumber: "",
              email: "",
              panCardNumber: "",
              passportNumber: "",
              passportExpiry: "",
              age: childAges[i] ?? "",
            });
          }
          return roomGuests;
        }
      );
      setGuestDetails(initialGuestDetails);
    }
  }, [bookingDetails, guestDetailsDraft]);

  const isValidEmail = (value) => {
    const v = String(value || "").trim().toLowerCase();
    if (!v) return false;

    const basicPattern =
      /^(?!.*\.\.)[a-z0-9._%+-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;
    if (!basicPattern.test(v)) return false;

    const allowedEndings = [".com", ".in", ".edu", ".tech", ".co.uk", ".co.in"];
    return allowedEndings.some((suffix) => v.endsWith(suffix));
  };

  const sanitizeName = (value) =>
    String(value || "")
      .replace(/[^A-Za-z\s]/g, "")
      .replace(/\s{2,}/g, " ");

  const isValidName = (value) => {
    const v = String(value || "").trim();
    return NAME_PATTERN.test(v);
  };

  const sanitizeMobile = (value) => String(value || "").replace(/\D/g, "").slice(0, 10);

  const isValidMobile = (value) => MOBILE_PATTERN.test(String(value || ""));

  const handleReviewBooking = async () => {
    setShowValidation(true);

    const isAnyGuestInvalid = guestDetails.some(
      (guest) =>
        !isValidName(guest.firstName) ||
        !isValidName(guest.lastName) ||
        (guest.guestType === "child" && (guest.age === "" || guest.age == null))
    );
    if (isAnyGuestInvalid) {
      toast.warning(
        "Please fill in all required guest details (Name and Age for children)"
      );
      return;
    }

    const missingOrInvalidEmail = guestDetails.some(
      (g) => !isValidEmail(g.email)
    );
    if (missingOrInvalidEmail) {
      toast.warning(
        "Every guest must have a valid email address"
      );
      return;
    }

    const lead = guestDetails.find(
      (g) =>
        g.roomIndex === 0 &&
        g.guestNumber === 1 &&
        g.guestType === "adult"
    );
    if (!isValidMobile(lead?.mobileNumber)) {
      toast.warning("Lead guest mobile number must be exactly 10 digits");
      return;
    }

    const itineraryCode = bookingDetails?.itineraryCode;
    if (!itineraryCode) {
      toast.error("Itinerary code missing. Please go back and select room again.");
      return;
    }
    const selectedRooms =
      Array.isArray(selectedRoomSummary?.selectedRooms) &&
      selectedRoomSummary.selectedRooms.length > 0
        ? selectedRoomSummary.selectedRooms
        : selectedRoomSummary?.roomId && selectedRoomSummary?.rateId
          ? [
              {
                rateId: selectedRoomSummary.rateId,
                roomId: selectedRoomSummary.roomId,
              },
            ]
          : [];

    if (
      !selectedRooms.length ||
      selectedRooms.some((r) => !r?.rateId || !r?.roomId)
    ) {
      toast.error("Room information missing. Please go back and select room again.");
      return;
    }

    const occupancyCount = bookingDetails?.query?.occupancies?.length ?? 0;
    if (occupancyCount > 0 && selectedRooms.length !== occupancyCount) {
      toast.error(
        "Selected rooms do not match your room count. Please go back and select rooms again."
      );
      return;
    }

    const leadMobileDigits = sanitizeMobile(lead.mobileNumber);

    const mapGuestToApi = (g) => {
      const isLead =
        g.roomIndex === 0 &&
        g.guestNumber === 1 &&
        g.guestType === "adult";
      const emailTrim = (g.email || "").trim();
      const panTrim = (g.panCardNumber || "").trim();
      const passTrim = (g.passportNumber || "").trim();
      const expTrim = (g.passportExpiry || "").trim();

      const guest = {
        title: g.title || "",
        firstName: g.firstName,
        lastName: g.lastName,
        isLeadGuest: isLead,
        type: g.guestType === "child" ? "child" : "adult",
        email: emailTrim,
        isdCode: "91",
        contactNumber: leadMobileDigits,
      };

      if (panTrim) guest.panCardNumber = panTrim.toUpperCase();
      if (passTrim) guest.passportNumber = passTrim;
      if (expTrim) guest.passportExpiry = expTrim;

      return guest;
    };

    const allocatePayload = {
      query: bookingDetails.query,
      traceId: bookingDetails.traceId || "",
      rooms: selectedRooms.map((room, roomIndex) => ({
        rateId: room.rateId,
        roomId: room.roomId,
        guests: guestDetails
          .filter((g) => g.roomIndex === roomIndex)
          .map(mapGuestToApi),
      })),
      specialRequests: null,
    };

    setIsSubmitting(true);
    try {
      const response = await searchAPI.allocateItineraryGuests(
        itineraryCode,
        allocatePayload
      );
      const ok =
        response?.status === "success" ||
        Number(response?.statusCode) === 200 ||
        response?.statusCode === "200";
      if (!ok) {
        toast.error(response?.message || "Allocation failed");
        return;
      }
      toast.success(response?.message || "Guests saved. Review your booking.");
      navigate("/review-booking", {
        state: {
          itineraryCode,
          traceId: bookingDetails.traceId || "",
          hotelInfo: passedHotelInfo,
          selectedRoomSummary,
          bookingDetails,
          guestDetailsDraft: guestDetails,
          allocateResponse: response,
        },
      });
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          "Could not save guest details. Try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalAmount = selectedRoomSummary?.totalPrice ?? 0;
  const currencySymbol =
    selectedRoomSummary?.currency === "INR" || !selectedRoomSummary
      ? "₹"
      : selectedRoomSummary.currency + " ";

  const checkIn = bookingDetails?.query?.checkIn;
  const checkOut = bookingDetails?.query?.checkOut;
  const checkInDisplay = formatDateDDMMYYYY(checkIn);
  const checkOutDisplay = formatDateDDMMYYYY(checkOut);
  const heroUrl =
    passedHotelInfo?.heroImage || passedHotelInfo?.images?.[0];

  if (!bookingDetails) {
    return (
      <Box sx={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center", mt: 2 }}>
        <Typography variant="h6" color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  return (
    <>
      {isSubmitting && <Loader />}
      <ToastContainer position="top-right" autoClose={3000} />
      <Box
        sx={{
          minHeight: "100vh",
          py: 4,
          mt: 2,
          bgcolor: "#f0f4f8",
          backgroundImage:
            "linear-gradient(180deg, #f0f4f8 0%, #ffffff 380px, #f5f7fa 100%)",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            px: { xs: 2, sm: 3, md: 4 },
            py: 3,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: "0 0 24px rgba(0,0,0,0.06)",
            overflow: "visible",
          }}
        >
          {heroUrl && (
            <Card
              elevation={0}
              sx={{
                mb: 3,
                mt: { xs: 2.5, sm: 3, md: 3.5 },
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} alignItems="stretch">
                <CardMedia
                  component="img"
                  sx={{ width: { xs: "100%", sm: 200 }, height: { xs: 140, sm: "auto" }, minHeight: 120, objectFit: "cover" }}
                  image={heroUrl}
                  alt=""
                />
                <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <HotelOutlinedIcon sx={{ color: themeAccent }} />
                    <Typography variant="overline" sx={{ color: themeAccent, fontWeight: 700, letterSpacing: 1 }}>
                      Your selection
                    </Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight={700} color={themePrimary}>
                    {passedHotelInfo?.name || "Hotel"}
                  </Typography>
                  {selectedRoomSummary?.roomNames?.length > 1 ? (
                    <Stack component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }} spacing={0.25}>
                      {selectedRoomSummary.roomNames.map((name, i) => (
                        <Typography
                          key={i}
                          component="li"
                          variant="body2"
                          color="text.secondary"
                        >
                          {name}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    selectedRoomSummary?.roomName && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {selectedRoomSummary.roomName}
                      </Typography>
                    )
                  )}
                  {(checkIn || checkOut) && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                      <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: themeAccent }} />
                      <Typography variant="body2">
                        <strong>{checkInDisplay}</strong>
                        <Box component="span" sx={{ mx: 1, color: "divider" }}>→</Box>
                        <strong>{checkOutDisplay}</strong>
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Card>
          )}

          <Box
            sx={{
              position: "relative",
              width: "100%",
              mt: heroUrl ? { xs: 0, sm: 0.5 } : { xs: 2.5, sm: 3, md: 3.5 },
            }}
          >
            <GuestDecorLeftColumn />
            <GuestDecorRightColumn />

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                maxWidth: { xs: "100%", sm: 640, md: 720, lg: 800 },
                mx: "auto",
                px: { xs: 0, sm: 1, md: 2 },
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: themeAccent,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textAlign: { xs: "left", sm: "center" },
                  display: "block",
                }}
              >
                Guest information
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: themePrimary,
                  fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  mb: 0.5,
                  textAlign: { xs: "left", sm: "center" },
                }}
              >
                Who&apos;s travelling?
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 560, mx: { sm: "auto" }, textAlign: { xs: "left", sm: "center" } }}
              >
                Enter details exactly as on your ID. Each guest needs a unique email. The lead guest
                will receive booking updates on the mobile number you add below.
              </Typography>

              {guestDetails.length === 0 ? (
                <Typography color="text.secondary">Preparing form…</Typography>
              ) : (
                guestDetails.map((guest, guestIndex) => {
                  const roomIndex = guest.roomIndex;
                  const prevGuest = guestIndex > 0 ? guestDetails[guestIndex - 1] : null;
                  const showRoomHeading =
                    !prevGuest || prevGuest.roomIndex !== roomIndex;
                  const roomGuests = guestDetails.filter(
                    (g) => g.roomIndex === roomIndex
                  );
                  const adultsInRoom = roomGuests.filter(
                    (g) => g.guestType === "adult"
                  );
                  const childrenInRoom = roomGuests.filter(
                    (g) => g.guestType === "child"
                  );
                  let displayNumber;
                  if (guest.guestType === "adult") {
                    displayNumber =
                      adultsInRoom.findIndex(
                        (g) => g.guestNumber === guest.guestNumber
                      ) + 1;
                  } else {
                    displayNumber =
                      childrenInRoom.findIndex(
                        (g) => g.guestNumber === guest.guestNumber
                      ) + 1;
                  }
                  const isLead =
                    guest.roomIndex === 0 &&
                    guest.guestNumber === 1 &&
                    guest.guestType === "adult";

                  return (
                    <React.Fragment key={guestIndex}>
                    {showRoomHeading && (
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color={themePrimary}
                        sx={{
                          mt: guestIndex > 0 ? 3 : 0,
                          mb: 1.5,
                          textAlign: { xs: "left", sm: "center" },
                        }}
                      >
                        Room {roomIndex + 1}
                        {selectedRoomSummary?.roomNames?.[roomIndex]
                          ? ` — ${selectedRoomSummary.roomNames[roomIndex]}`
                          : ""}
                      </Typography>
                    )}
                    <Paper
                      elevation={0}
                      sx={{
                        mb: 2.5,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderLeft: "4px solid",
                        borderLeftColor: isLead ? themeAccent : "rgba(27, 60, 115, 0.25)",
                        bgcolor: "#fafbfd",
                        transition: "box-shadow 0.2s",
                        "&:hover": {
                          boxShadow: "0 4px 20px rgba(27, 60, 115, 0.08)",
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={1}
                        sx={{ mb: 2.5 }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              bgcolor: isLead ? "rgba(0, 113, 194, 0.12)" : "rgba(27, 60, 115, 0.08)",
                              color: themePrimary,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 800,
                              fontSize: "0.95rem",
                            }}
                          >
                            {guestIndex + 1}
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700} color={themePrimary}>
                              {guest.guestType === "adult" ? "Adult" : "Child"} {displayNumber}
                            </Typography>
                            {isLead && (
                              <Chip
                                size="small"
                                label="Lead guest"
                                sx={{
                                  height: 22,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  bgcolor: "rgba(0, 113, 194, 0.12)",
                                  color: themeAccent,
                                }}
                              />
                            )}
                          </Box>
                        </Stack>
                      </Stack>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5, fontWeight: 600 }}
                      >
                        <PersonOutlineIcon sx={{ fontSize: 16 }} /> Name
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={guest.guestType === "child" ? 2 : 2}>
                          <FormControl fullWidth size="medium">
                            <InputLabel>Title</InputLabel>
                            <Select
                              label="Title"
                              value={guest.title}
                              onChange={(e) =>
                                updateGuestField(
                                  roomIndex,
                                  guest.guestNumber,
                                  guest.guestType,
                                  "title",
                                  e.target.value
                                )
                              }
                            >
                              <MenuItem value="">Select</MenuItem>
                              <MenuItem value="Mr">Mr.</MenuItem>
                              <MenuItem value="Ms">Ms.</MenuItem>
                              <MenuItem value="Mrs">Mrs.</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={guest.guestType === "child" ? 4 : 5}>
      <TextField
                            fullWidth
                            size="medium"
                            label="First name"
                            value={guest.firstName}
                            error={showValidation && !isValidName(guest.firstName)}
                            onChange={(e) =>
                              updateGuestField(
                                roomIndex,
                                guest.guestNumber,
                                guest.guestType,
                                "firstName",
                                sanitizeName(e.target.value)
                              )
                            }
                            helperText={
                              showValidation && !isValidName(guest.firstName)
                                ? "Only letters are allowed"
                                : " "
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={guest.guestType === "child" ? 4 : 5}>
                          <TextField
                            fullWidth
                            size="medium"
                            label="Last name"
                            value={guest.lastName}
                            error={showValidation && !isValidName(guest.lastName)}
                            onChange={(e) =>
                              updateGuestField(
                                roomIndex,
                                guest.guestNumber,
                                guest.guestType,
                                "lastName",
                                sanitizeName(e.target.value)
                              )
                            }
                            helperText={
                              showValidation && !isValidName(guest.lastName)
                                ? "Only letters are allowed"
                                : " "
                            }
                          />
                        </Grid>
                        {guest.guestType === "child" && (
                          <Grid item xs={12} sm={2}>
                            <TextField
                              fullWidth
                              size="medium"
                              label="Age"
                              type="number"
                              value={guest.age}
                              onChange={(e) =>
                                updateGuestField(
                                  roomIndex,
                                  guest.guestNumber,
                                  guest.guestType,
                                  "age",
                                  e.target.value
                                )
                              }
                              inputProps={{ min: 0, max: 17 }}
                            />
                          </Grid>
                        )}
                      </Grid>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5, fontWeight: 600 }}
                      >
                        <EmailOutlinedIcon sx={{ fontSize: 16 }} /> Email
                      </Typography>
                      <TextField
                        fullWidth
                        size="medium"
                        required
                        label="Email address"
                        type="email"
                        value={guest.email}
                        error={showValidation && !isValidEmail(guest.email)}
                        onChange={(e) =>
                          updateGuestField(
                            roomIndex,
                            guest.guestNumber,
                            guest.guestType,
                            "email",
                            e.target.value
                          )
                        }
                        placeholder="name@example.com"
                        helperText={
                          showValidation && !isValidEmail(guest.email)
                            ? "Please enter a valid email"
                            : "Required for each traveller"
                        }
                        sx={{ mb: isLead ? 2 : 0 }}
                      />

                      {isLead && (
                        <>
                          <Divider sx={{ my: 2.5 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5, fontWeight: 600 }}
                          >
                            <PhoneIphoneIcon sx={{ fontSize: 16 }} /> Contact
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            SMS and booking confirmations will be sent to this number.
                          </Typography>
                          <TextField
                            fullWidth
                            size="medium"
                            required
                            label="Mobile number"
                            value={guest.mobileNumber}
                            error={showValidation && !isValidMobile(guest.mobileNumber)}
                            onChange={(e) =>
                              updateGuestField(
                                roomIndex,
                                guest.guestNumber,
                                guest.guestType,
                                "mobileNumber",
                                sanitizeMobile(e.target.value)
                              )
                            }
                            placeholder="10-digit number"
                            helperText={
                              showValidation && !isValidMobile(guest.mobileNumber)
                                ? "Enter a valid 10-digit mobile number"
                                : " "
                            }
                            inputProps={{ inputMode: "numeric", maxLength: 10 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box
                                    component="span"
                                    sx={{ display: "flex", alignItems: "center", mr: 0.5 }}
                                  >
                                    <img
                                      src="https://flagcdn.com/w20/in.png"
                                      alt=""
                                      style={{ marginRight: 8, width: 20 }}
                                    />
                                    +91
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 2 }}
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5, fontWeight: 600 }}
                          >
                            <BadgeOutlinedIcon sx={{ fontSize: 16 }} /> ID (optional)
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="medium"
                                label="PAN"
                                value={guest.panCardNumber}
                                onChange={(e) =>
                                  updateGuestField(
                                    roomIndex,
                                    guest.guestNumber,
                                    guest.guestType,
                                    "panCardNumber",
                                    e.target.value
                                  )
                                }
                                inputProps={{
                                  maxLength: 10,
                                  style: { textTransform: "uppercase" },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="medium"
                                label="Passport no."
                                value={guest.passportNumber}
                                onChange={(e) =>
                                  updateGuestField(
                                    roomIndex,
                                    guest.guestNumber,
                                    guest.guestType,
                                    "passportNumber",
                                    e.target.value
                                  )
                                }
                                inputProps={{ style: { textTransform: "uppercase" } }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="medium"
                                label="Passport expiry"
                                type="date"
                                value={guest.passportExpiry}
                                onChange={(e) =>
                                  updateGuestField(
                                    roomIndex,
                                    guest.guestNumber,
                                    guest.guestType,
                                    "passportExpiry",
                                    e.target.value
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </Paper>
                    </React.Fragment>
                  );
                })
              )}

            <Box sx={{ mt: { xs: 3, md: 4 } }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                borderRadius: 2,
                border: "2px solid",
                borderColor: "rgba(0, 113, 194, 0.2)",
                background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                boxShadow: "0 12px 40px rgba(27, 60, 115, 0.08)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ flex: { sm: "1 1 auto" }, minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{ color: themeAccent, fontWeight: 700, letterSpacing: 1, display: "block" }}
                  >
                    Trip total
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#0d7a3e",
                      my: 0.5,
                      fontSize: { xs: "1.65rem", sm: "1.85rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    {currencySymbol}
                    {totalAmount?.toLocaleString("en-IN") ?? "0"}
                  </Typography>
                  {selectedRoomSummary?.roomNames?.length > 1 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {selectedRoomSummary.roomNames.join(" · ")}
                    </Typography>
                  ) : (
                    selectedRoomSummary?.roomName && (
                      <Typography variant="body2" color="text.secondary" noWrap title={selectedRoomSummary.roomName}>
                        {selectedRoomSummary.roomName}
                      </Typography>
                    )
                  )}
                </Box>
                <Divider sx={{ display: { xs: "block", sm: "none" }, width: "100%" }} />
                <Divider
                  sx={{ display: { xs: "none", sm: "block" }, alignSelf: "stretch" }}
                  orientation="vertical"
                  flexItem
                />
                <Stack
                  spacing={1.5}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 220, md: 260 },
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleReviewBooking}
                    disabled={isSubmitting}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      py: 1.35,
                      fontSize: "1rem",
                      bgcolor: themePrimary,
                      boxShadow: "0 4px 14px rgba(27, 60, 115, 0.35)",
                      "&:hover": { bgcolor: "#142d56" },
                    }}
                  >
                    {isSubmitting ? "Saving…" : "Review booking"}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ textTransform: "none", fontWeight: 600, borderColor: "divider" }}
                    onClick={() => navigate(-1)}
                  >
                    Back to room selection
                  </Button>
                </Stack>
              </Stack>
            </Paper>
            </Box>
          </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default GuestDetail;
